import { FJEncodeMode, FJEncoderManager, FJDecoderManager, FJDecodeMode } from '@fj/codec';
import FJUtil from '../util/FJUtil.js';
import FJCoreConfig from '../core/FJCoreConfig.js';
import FJExporter from '../core/export/FJExporter.js';

const FJExportStore = {
    _isExport: false,
    _exportSize: { width: 1920, height: 1080 },
    _hasAudio: true,
    _exportOptions: {},
    _exporter: null,
    _status: 0,
    _timestampPerFrame: 0,
    _encodeVideoFrameCount: 0,
    _encodePerFrameRatio: 0,
    _progress: 0,
    _fps: FJCoreConfig.FPS,
    _videoElement: null,
    _subtitle: null,
    _detailManager: null,
    // 导出
    export: async function (options = {}) {
        this._exportOptions = options;
        this._fps = FJCoreConfig.FPS;
        this._detailManager = options.detailManager;
        this._videoElement = this._detailManager.getVideoElement();
        this._subtitle = this._detailManager.getSubtitle();
        if (options.exportSize && options.exportSize.width && options.exportSize.height) {
            this._exportSize = options.exportSize;
        }
        this._timestampPerFrame = 1e6 / this._fps;
        this._encodeVideoFrameCount = 0;
        this._progress = 0;
        const duration = this._detailManager.getDuration();
        const frameCount =
            Math.floor(duration) * this._fps + Math.floor((duration - Math.floor(duration)) / (1 / this._fps));
        this._encodePerFrameRatio = (1.0 / frameCount) * 0.5;

        this._startExport();
    },
    _startExport: function () {
        const { onComplete, onError, onProgress } = this._exportOptions;
        this._isExport = true;
        this._exporter = new FJExporter(
            this._videoElement,
            this._subtitle,
            data => this._exporterCallback(data),
            () => !FJEncoderManager.shouldWaitEncode(),
            () => FJEncoderManager.getValidFramesSize(),
        );
        const audioConfig = {
            bitrate: 256 * 1000,
            samplerate: FJUtil.getAudioSampleRate(),
        };
        const videoConfig = {
            fps: 30,
            width: this._exportSize.width,
            height: this._exportSize.height,
            presetIdx: 0, // h264编码参数，控制输出速度和质量。0代表最快，0-7依次对应"ultrafast", "superfast", "veryfast", "faster", "fast", "medium", "slow", "veryslow"
            codecIdx: 1, // 编码器索引，0代表vpx，1代表h264, 2代表gif
            formatIdx: 1, // 格式索引，0代表webm，1代表mp4, 2代表gif

            loop: 0, // GIF编码参数，0代表无限循环播放, 1代表播放一次
            mode: FJEncodeMode.WEBCODECS,
            quality: {
                crf: 20,
                bitrate: 10000000,
            },
            bitrateMode: 'variable', // h264编码参数，控制输出速度和质量。constant代表恒定码率，variable代表可变码率
            exportQuality: 1080,
            concurrentSize: 2,
        };

        const encoderOptions = {
            initCallback: async () => {
                if (this._hasAudio) {
                    this._startEncodeAudio().then(bool => {
                        if (!bool) {
                            onError('audio err');
                            this.stopExport();
                        }
                    });
                }
                // 开始输出渲染
                this._exporter.start(this._exportSize, this._fps);
            },
            doneCallback: data => {
                const blob = new Blob([data], { type: 'video/mp4' });

                onComplete(blob);
                FJEncoderManager.cancel();
            },
            chunkCallback: () => {},
            errorCallback: onError,
            encodeCallback: () => {
                this._progress += this._encodePerFrameRatio;
                onProgress && onProgress(this._progress);
            },
        };
        FJEncoderManager.initEncoder(videoConfig, audioConfig, encoderOptions);
    },
    stopExport: function () {
        this._isExport = false;
        this._exporter && this._exporter.clear();
    },
    getIsExporting: function () {
        return !!this._isExport;
    },
    /**
     * @desc 渲染器的回调函数
     * @param data
     * @private
     */
    _exporterCallback: function (data) {
        const options = this._exportOptions;
        const { onProgress, onError } = options;

        try {
            switch (data.type) {
                case 'board': {
                    this._status = data.boardIndex;
                    break;
                }
                case 'progress': {
                    // 渲染完成一帧
                    // 渲染和编码进度各站一半，因此需要乘0.5，视频帧占比 0.9
                    this._progress += data.progress * 0.5;
                    onProgress && onProgress(this._progress);
                    const frameCanvas = data.frame;
                    if (frameCanvas) {
                        // 传给编码器
                        let frameType = '';
                        let frame = null;

                        // 如果输出的是 gif, mac 编码后会出现色差
                        if (
                            typeof VideoFrame === 'undefined' ||
                            FJUtil.getBrowser() === 'FF' ||
                            FJUtil.getBrowser() === 'Safari'
                        ) {
                            const couldOptFrame = FJUtil.couldOptFrame();
                            frame = couldOptFrame
                                ? FJUtil.getImageBitmap(frameCanvas)
                                : frameCanvas
                                      .getContext('2d')
                                      .getImageData(0, 0, frameCanvas.width, frameCanvas.height);
                            frameType = couldOptFrame ? 'IMAGEBITMAP' : 'IMAGEDATA';
                        } else {
                            frameType = 'VIDEOFRAME';
                            frame = new VideoFrame(frameCanvas, {
                                alpha: 'discard',
                                displayWidth: frameCanvas.width,
                                displayHeight: frameCanvas.height,
                                duration: this._timestampPerFrame,
                                timestamp: this._timestampPerFrame * this._encodeVideoFrameCount++,
                            });
                        }

                        FJEncoderManager.addVideoFrame({
                            type: frameType,
                            data: frame,
                        });
                    } else {
                        onError && onError('Frame is null.');
                    }

                    break;
                }
                case 'error': {
                    // 渲染失败
                    onError && onError(data.error || data.errorMsg || 'exportBoardAtIndex error');
                    break;
                }
                case 'done': {
                    // 渲染完成
                    FJEncoderManager.markVideoFrameDecodeDone();

                    break;
                }
            }
        } catch (e) {
            console.log(e);
        }
    },

    _startEncodeAudio: async function () {
        if (!this._hasAudio) return false;
        FJEncoderManager.markAudioEncode(true);
        await new Promise(resolve => {
            const check = () => {
                if (FJEncoderManager.isVideoFrameEncodeDone()) {
                    resolve();
                } else {
                    requestAnimationFrame(() => {
                        check();
                    });
                }
            };
            check();
        });
        try {
            const file = await this._videoElement.getSourceBlob();
            const { startTime, endTime } = this._videoElement.getTimeData();
            const audioData = await FJDecoderManager.decodeAudioOverall(file, {
                start: startTime,
                end: endTime,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: FJDecodeMode.FFMPEG,
                speed: 1,
                fileType: 'video',
                ignoreNoValidDuration: true,
            });
            FJEncoderManager.addAudioChunk(audioData.buffer, true);
            FJEncoderManager.markAudioEncode(false);
            return true;
        } catch (error) {
            return false;
        }
    },
};

export default FJExportStore;
